import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/daetem9tto0ceqqnh3m0';

export default function SubscribeSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('loading');
    setFormError('');
    const form = e.currentTarget;
    const fd = new FormData(form);

    // anti-spam honeypot
    const hp = ((fd.get('contact_alt') as string) || '').trim();
    if (hp) {
      setStatus('ok');
      form.reset();
      return;
    }
    fd.delete('contact_alt');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = String(
        (parsed as { meta?: { message?: string; detail?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          (parsed as { meta?: { detail?: string } })?.meta?.detail ||
          ''
      );
      const code = String((parsed as { code?: string })?.code || '');
      const isSpam = /spam|form data is spam/i.test(responseText) || /spam|form data is spam/i.test(msg);

      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('ok');
        form.reset();
      } else {
        setStatus('error');
        setFormError(msg || '订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，订阅失败，请稍后重试。');
    }
  }

  return (
    <section id="subscribe" className="relative overflow-hidden py-12 md:py-16">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-primary-600 px-6 py-10 md:px-14 md:py-14">
          <div className="dot-bg absolute inset-0 opacity-40" aria-hidden="true"></div>
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent-400/30 blur-3xl" aria-hidden="true"></div>
          <div className="relative grid items-center gap-8 lg:grid-cols-2">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-background-50">
                <i className="ri-mail-send-fill" aria-hidden="true"></i>
                每日速报
              </p>
              <h3 className="mt-4 text-2xl font-black leading-tight text-background-50 md:text-3xl">
                订阅「反差吃瓜」每日速报
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-background-100 md:text-base">
                每天下午固定推送当天最值得吃的反差瓜速览，三分钟看完不迷路。零广告骚扰，随时可退订。
              </p>
            </div>

            <div className="rounded-2xl bg-background-50 p-6 md:p-7">
              {status === 'ok' ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-700">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <p className="font-bold text-foreground-950">订阅成功！</p>
                  <p className="text-sm text-foreground-600">感谢关注，每日吃瓜速报马上送到你的邮箱。</p>
                </div>
              ) : (
                <form data-readdy-form="吃瓜速报订阅" onSubmit={onSubmit} className="flex flex-col gap-4">
                  <label htmlFor="sub-mail" className="text-sm font-semibold text-foreground-800">
                    订阅邮箱
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" aria-hidden="true"></i>
                    <input
                      id="sub-mail"
                      type="email"
                      name="email"
                      required
                      placeholder="name@example.com"
                      className="w-full rounded-xl border border-background-300 bg-background-100 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div className="hp-guard" aria-hidden="true">
                    <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                  </div>
                  {status === 'error' && (
                    <p className="text-sm font-semibold text-primary-600">
                      <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
                      {formError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:opacity-60"
                  >
                    {status === 'loading' ? (
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        正在提交…
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-send-plane-fill" aria-hidden="true"></i>
                        订阅每日速报
                      </span>
                    )}
                  </button>
                  <p className="text-center text-xs text-foreground-500">
                    提交即表示同意接收反差吃瓜的每日速报邮件
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}